import { supabase } from '../lib/supabase'
import type { AppRole, Profile, SectionPermission } from '../lib/types'

export type SectionCapability = keyof Pick<SectionPermission, 'can_read' | 'can_write' | 'can_log' | 'can_maintain' | 'can_trend' | 'can_download' | 'can_backup'>
export interface PermissionService { role: AppRole; isAdmin: boolean; isEngineer: boolean; can: (sectionId: number, permission: SectionCapability) => boolean }
const permissionKeys: SectionCapability[] = ['can_read', 'can_write', 'can_log', 'can_maintain', 'can_trend', 'can_download', 'can_backup']

const normalizePermission = (row: Partial<SectionPermission>): SectionPermission => {
  const elevatedCapability = permissionKeys.some((key) => key !== 'can_read' && row[key] === true)
  return {
    id: Number(row.id ?? 0),
    user_id: String(row.user_id ?? ''),
    section_id: Number(row.section_id ?? 0),
    can_read: row.can_read === true || elevatedCapability,
    can_write: row.can_write === true,
    can_log: row.can_log === true,
    can_maintain: row.can_maintain === true,
    can_trend: row.can_trend === true,
    can_download: row.can_download === true,
    can_backup: row.can_backup === true,
    created_at: String(row.created_at ?? ''),
    updated_at: String(row.updated_at ?? ''),
  }
}

export async function loadPermissions(profile: Profile): Promise<PermissionService> {
  const elevated = profile.role === 'admin' || profile.role === 'engineer'
  if (!permissionKeys.length) throw new Error('APL Connect authorization configuration is invalid.')

  let permissions: SectionPermission[] = []
  if (!elevated) {
    const result = await supabase
      .from('user_section_permissions')
      .select('id,user_id,section_id,can_read,can_write,can_log,can_maintain,can_trend,can_download,can_backup,created_at,updated_at')
      .eq('user_id', profile.id)

    if (result.error) throw new Error(`Unable to load section permissions: ${result.error.message}`)
    permissions = (result.data ?? []).map((row) => normalizePermission(row as Partial<SectionPermission>))
  }

  const can = (sectionId: number, permission: SectionCapability) => {
    if (elevated) return true
    const item = permissions.find((entry) => Number(entry.section_id) === Number(sectionId))
    if (!item || item.can_read !== true) return false
    return permission === 'can_read' || item[permission] === true
  }

  return { role: profile.role, isAdmin: profile.role === 'admin', isEngineer: profile.role === 'engineer', can }
}
